//
//  DevicesPanel.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 16, 2026
//  Author: Bob Keathley
//
//  Shared device-sessions panel (spec 6m) for self, tenant admin and platform
//  owner; the caller's routes choose which. Lists active sessions only, with an
//  optional single-machine filter. Revoke is shown only when the caller may.
//
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { isOk } from '../api/envelope'
import type { DeviceSession } from '../api/types'

const stamp = (s: number | null | undefined) => (s ? new Date(s * 1000).toLocaleString() : 'never')

/** §6m device sessions of one person. The caller picks the routes, so one panel serves every case:
 *  self (`/users/me/devices`, `DELETE /auth/device/{id}`), tenant admin (`/users/{id}/devices[/{did}]`)
 *  and platform owner (`/platform/users/{id}/devices[/{did}]`). Active sessions only.
 *  `onlyMachine` narrows to one machine (the Machines page). */
export default function DevicesPanel({ token, listPath, revokePath, canRevoke, onlyMachine }: {
  token: string
  listPath: string
  revokePath: (deviceId: string) => string
  canRevoke: boolean
  onlyMachine?: string
}) {
  const [items, setItems] = useState<DeviceSession[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(async () => {
    const r = await api<{ items: DeviceSession[] }>(listPath, {}, token)
    if (isOk(r)) { setItems(r.data.items.filter((d) => !onlyMachine || d.machine_id === onlyMachine)); setError(null) } else { setItems([]); setError(r.error.message) }
  }, [listPath, token, onlyMachine])
  useEffect(() => { load() }, [load])
  async function revoke(d: DeviceSession) {
    if (!confirm(`Revoke ${d.device_label}? The next refresh fails; an access token already issued works until it expires (15 min).`)) return
    const r = await api<{ revoked: boolean }>(revokePath(d.id), { method: 'DELETE' }, token)
    if (!isOk(r)) setError(r.error.message); else load()
  }
  return (
    <div aria-label="Devices">
      {error && <p className="err" role="alert">{error}</p>}
      <table>
        <thead><tr><th>Device</th><th>OS user</th><th>Last used</th><th>Access expires</th><th>Session expires</th><th></th></tr></thead>
        <tbody>
          {items === null && <tr><td colSpan={6} className="hint">Loading…</td></tr>}
          {items && items.length === 0 && <tr><td colSpan={6} className="hint">No active device sessions.</td></tr>}
          {items?.map((d) => (
            <tr key={d.id}>
              <td>{d.device_label}</td>
              <td className="mono">{d.os_user}</td>
              <td className="hint">{stamp(d.last_used_at)}</td>
              <td className="hint">{stamp(d.access_expires_at)}</td>
              <td className="hint">{stamp(d.refresh_expires_at)}</td>
              <td style={{ textAlign: 'right' }}>{canRevoke && <button className="btn danger sm" onClick={() => revoke(d)}>Revoke</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
